import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, Renderer2, signal } from "@angular/core";
import { BehaviorSubject, Subscription, tap } from "rxjs";
import { Stack } from "src/app/common/dataStructures/stack";
import { PaginationOperationEnum } from "src/app/components/table/table.component";
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from "src/app/models/api/jsonhal.model";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from "src/app/models/componentInputs/tablePageInfo.interface";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";
import { PageWithFiltersEnum } from "src/app/models/filters/pageWithFilters.enum";
import { ISelectedFilters } from "src/app/models/filters/selectedFilters.interface";
import { IPaginationNavigationModel } from "src/app/models/paginationNavigation.model";
import { ISelectOption } from "src/app/models/selectOptions";
import { SortColumn } from "src/app/models/sortColumn.model";
import { ITableColumn } from "src/app/models/tableColumns";
import { DisplayCamelCaseStringPipe } from "src/app/pipes/display-camel-case-string/display-camel-case-string.pipe";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { FiltersService } from "src/app/services/filters/filters.service";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { agentStepsActivityTableColumns } from "src/app/models/tableColumns/aiAgentStepActivityTableColumn";
import { AiAgentStepActivityTableRowViewModel } from "src/app/models/viewModels/aiAgentStepActivityTableRowViewModel";
import { AgentClientApplication, AgentClientApplicationIcon, AgentStepModel, BaseAgentStepModel } from "src/app/models/api/agentStep.model";
import { AiAgentStepsHttpService } from "src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service";
import { formatFromDateWithSeconds } from "src/app/common/datetime_utility";
import { AiActivityFiltersModel } from "src/app/models/filters/implementations/pageFilters/aiActivityFilters.model";
import { getAiServiceLogoPath } from "src/app/common/aiServiceUtils";
import { ActiveFiltersHeightService } from "src/app/services/infra/activeFiltersHeight/active-filters-height.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SidePanelService } from "../../../features/side-panel/services/side-panel.service";
import { AgentStepsActionsService } from "src/app/services/actions/agent-steps/agent-steps-actions.service";

@Component({
    selector: "app-enterprise-ai-activity",
    templateUrl: "./enterprise-ai-activity.component.html",
    styleUrls: ["./enterprise-ai-activity.component.scss"],
})
export class EnterpriseAiActivityComponent implements OnInit, OnDestroy {
    public columns = signal<ITableColumn[]>(agentStepsActivityTableColumns);
    public agentStepResourcesCurrentPage$: BehaviorSubject<AiAgentStepActivityTableRowViewModel[]> = new BehaviorSubject<
        AiAgentStepActivityTableRowViewModel[]
    >([]);
    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public showNoData = false;
    public isFiltersMenuOpen = false;

    public readonly defaultPageSize: number;
    public pageFilters: AiActivityFiltersModel;
    public optionsAreSelected = false;
    public pageInfo$: BehaviorSubject<ITablePageInfo>;
    public totalItems = 0;
    public resourcesCount: BehaviorSubject<number> = new BehaviorSubject(-1);
    public featureFlagTypes = FeatureFlagTypes;

    public permissionsEnum = PermissionEnum;

    private _currentAgentStepResourcesById: {
        [agentStepResourceId: string]: BaseAgentStepModel;
    } = {};
    private _notAvailableString: string;

    private _paginationNavigation: IPaginationNavigationModel;

    private _filtersOnChangeSubscription$: Subscription;
    private _firstPageSubscription$: Subscription;
    private _fetchByPageSubscription$: Subscription;
    private _sortOnChangeSubscription$: Subscription;

    constructor(
        public permissionsService: PermissionsService,
        public featureFlagsService: FeatureFlagsService,
        private _sidePanel: SidePanelService,
        private _logger: LoggerService,
        private _aiActivityHttpService: AiAgentStepsHttpService,
        private _filtersService: FiltersService,
        private _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _activeFiltersHeightService: ActiveFiltersHeightService,
        private _renderer: Renderer2,
        private _el: ElementRef,
        private _destroyRef: DestroyRef,
        private _agentStepsActionsService: AgentStepsActionsService,
    ) {
        this.defaultPageSize = this._aiActivityHttpService.defaultPageSize;
        this._notAvailableString = this._labelsProviderPipe.transform("common.notAvailable");
        this._initializePageInfo();
    }

    ngOnInit(): void {
        this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.AiActivity) as AiActivityFiltersModel;

        this._subscribeToHandleFiltersOnChange();
        this._subscribeToSortOnChange();

        this._activeFiltersHeightService.height$
            .pipe(
                tap((height: number) => this._renderer.setStyle(this._el.nativeElement, "--applied-filters-height", `${height}px`, 1)),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._filtersOnChangeSubscription$.unsubscribe();
        this._sortOnChangeSubscription$.unsubscribe();
    }

    public tablesFiltersChange(selectedOptions: ISelectOption[]) {
        this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
    }

    public onPagination(operation: PaginationOperationEnum) {
        if (!this.isLoading$.value) {
            if (operation == PaginationOperationEnum.next) {
                this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
            }

            const pageUrl =
                operation == PaginationOperationEnum.next ? this._paginationNavigation.nextPage : this._paginationNavigation.previousPage.pop();
            if (pageUrl) {
                this._doPaginationWhenTokenExist(pageUrl);
            }
        }
    }

    public filtersOnMenuStateChange(menuState: boolean) {
        this.isFiltersMenuOpen = menuState;
    }

    public rowSelected(selection: AiAgentStepActivityTableRowViewModel): void {
        this._sidePanel.openAgentStepDetails(selection);
    }

    public rowUnselected = () => this._sidePanel.close();

    private _subscribeToHandleFiltersOnChange() {
        this._filtersOnChangeSubscription$ = this.pageFilters.onUpdate$.subscribe((selectedFilters) => {
            this.resourcesCount.next(-1);
            this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
            this._fetchFirstPage(selectedFilters, this.pageFilters.sortedColumn$.value);
        });
    }

    private _subscribeToSortOnChange() {
        this._sortOnChangeSubscription$ = this.pageFilters.sortedColumn$.subscribe((newSort) => {
            this._fetchFirstPage(this.pageFilters.getSelectedFilters(), newSort);
        });
    }

    private _fetchByPage(continuationToken: string) {
        this._cancelOngoingHttpRequest();
        this._tableBehaviorChangeBeforeFetch();

        this._fetchByPageSubscription$ = this._aiActivityHttpService.getAgentStepsByContinuationToken(continuationToken).subscribe(
            (response) => this._handleAgentStepCollectionResponse(response),
            (error) => this._fetchRequestOnError(error),
        );
    }

    private _fetchFirstPage(filterOptions: ISelectedFilters, sortData?: SortColumn) {
        this._cancelOngoingHttpRequest();
        this._tableBehaviorChangeBeforeFetch();

        this._firstPageSubscription$ = this._aiActivityHttpService.startGetAgentSteps(filterOptions, sortData).subscribe({
            next: (response) => this._handleAgentStepCollectionResponse(response),
            error: (err) => this._fetchRequestOnError(err),
        });
    }

    private async _handleAgentStepCollectionResponse(response: JsonHalCollection<AgentStepModel>) {
        this._handlePaginationUrls(response._links);
        this._handlePageInfo(response.page);

        const items = response._embedded.items;
        this._currentAgentStepResourcesById = {};

        if (!items || items.length == 0) {
            this.showNoData = true;
            this.agentStepResourcesCurrentPage$.next([]);
        } else {
            this.showNoData = false;
            const agentStepViewModelTemp = items.map((item) => this._transformResourceToViewModel(item));
            this.agentStepResourcesCurrentPage$.next(agentStepViewModelTemp);
            await this._agentStepsActionsService.setActionsForAgentSteps(items, agentStepViewModelTemp);
        }
        this.isLoading$.next(false);
    }

    private _cancelOngoingHttpRequest() {
        if (this._firstPageSubscription$) {
            this._firstPageSubscription$.unsubscribe();
        }

        if (this._fetchByPageSubscription$) {
            this._fetchByPageSubscription$.unsubscribe();
        }
    }

    private _fetchRequestOnError(error: HttpErrorResponse) {
        this.agentStepResourcesCurrentPage$.next([]);
        this.isLoading$.next(false);
        this.showNoData = true;
        this._httpErrorHandlerService.handle(error);
    }

    private _tableBehaviorChangeBeforeFetch() {
        this.agentStepResourcesCurrentPage$.next([]);
        this.isLoading$.next(true);
    }

    private _handlePaginationUrls(links: JsonHalLinks) {
        if (!this._paginationNavigation) {
            this._paginationNavigation = {
                currentPage: links.self.href,
                nextPage: links.next?.href,
                previousPage: new Stack<string>(),
            } as IPaginationNavigationModel;
        } else {
            this._paginationNavigation.currentPage = links.self.href;
            this._paginationNavigation.nextPage = links.next?.href;
        }
    }

    private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
        this.totalItems = pageInfo.totalItems;
        const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

        if (!this.pageInfo$) {
            this.pageInfo$ = new BehaviorSubject(tablePageInfo);
        } else {
            this.pageInfo$.next(tablePageInfo);
        }
    }

    private _transformResourceToViewModel(step: AgentStepModel): AiAgentStepActivityTableRowViewModel {
        this._currentAgentStepResourcesById[step.id] = step;
        return {
            rowId: step.id,
            id: step.id,
            integrationId: step.integration.id,
            actorName: step.mainActor.displayName,
            timestamp: step.timestamp ? formatFromDateWithSeconds(step.timestamp) : this._notAvailableString,
            stepType: this._displayCamelCaseStringPipe.transform(step.type),
            threadId: step.thread.id,
            menuActions: [],
            clickable: false,
            platformIcon: getAiServiceLogoPath(step.service),
            application: this._displayCamelCaseStringPipe.transform(AgentClientApplication[step.clientContext.clientApplication]),
            agentName: this._displayCamelCaseStringPipe.transform(step.botDetails.displayName),
            step: step,
            clientApplicationIcon: Object.values(AgentClientApplicationIcon).includes(
                step.clientContext.clientApplication.toString() as AgentClientApplicationIcon,
            )
                ? `assets/images/logo/agent-client-app-${step.clientContext.clientApplication}.svg`
                : "assets/images/logo/agent-client-app-other.svg",
            clientApplication: Object.values(AgentClientApplication).includes(step.clientContext.clientApplication as AgentClientApplication)
                ? this._displayCamelCaseStringPipe.transform(AgentClientApplication[step.clientContext.clientApplication])
                : this._displayCamelCaseStringPipe.transform(step.clientContext.clientApplication),
        } as AiAgentStepActivityTableRowViewModel;
    }

    private _doPaginationWhenTokenExist(url: string) {
        const continuationToken = this._tokenExtractor(url);

        if (continuationToken) {
            this._fetchByPage(continuationToken);
        }
    }

    private _tokenExtractor(url: string): string | null {
        const continuationToken = new URLSearchParams(url.slice(url.indexOf("?"))).get("continuationToken");
        if (!continuationToken) {
            this._logger.error("Failed to extract continuation token from json hal next activity resource page link");
            return null;
        }

        return continuationToken;
    }

    private _initializePageInfo() {
        this.pageInfo$ = new BehaviorSubject({
            number: 1,
            currSize: 0,
            maxSize: this._aiActivityHttpService.defaultPageSize,
            totalItems: 0,
            totalPages: 1,
        });
    }
}
